import { TaskUtility } from 'src/app/task/task.utility';
import { TranslateService } from '@ngx-translate/core';
import { Component, OnInit, Input, OnDestroy } from '@angular/core';
import { UserService } from 'src/app/core/service/user.service';
import { sortBy } from 'lodash';
import moment from 'moment';
import { MessengerService, MessageSubject } from 'src/app/core/service/messenger.service';
import { TaskCardList, TaskCard } from './taskcard';
import { Subscription } from 'rxjs';
import { LanguageService } from 'src/app/core/service/language.service';
import { User } from 'src/app/user/model/user';
import { IntlService } from '@progress/kendo-angular-intl';
import { SettingService } from 'src/app/core/service/setting.service';
import { AuthenticationService } from 'src/app/core/authentication/authentication.service';

@Component({
  standalone: false,
  selector: 'app-task-card',
  templateUrl: './task-card.component.html',
  styleUrls: ['./task-card.component.scss']
})
export class TaskCardComponent implements OnInit, OnDestroy
{
  @Input() public channelId: number;

  /**
   * Data model for this component.
   */
  public model: TaskCardList;

  /**
   * Calendar texts to use with moment translations
   */
  public calendarTexts: Array<{ text: string }> = [];

  /**
   * Channel's users
   */
  public users: any;

  public messagesEnabled = false;

  /**
   * User's roles
   */
  private roles: Array<{ text: string; value: string }>;

  private messagingSubscription: Subscription;

  constructor(
    private userService: UserService,
    private translate: TranslateService,
    private messengerService: MessengerService,
    private taskUtility: TaskUtility,
    private languageService: LanguageService,
    private settingService: SettingService,
    private authenticationService: AuthenticationService,
    public intl: IntlService)
  {
    // This message will be trigerred when the task details screen is closed.
    // If any update occurs, the card view must be reloaded.
    this.messagingSubscription = this.messengerService.receiveMessage().subscribe({
      next: (message) =>
      {
        if (message.type === 'refreshTaskCardView' && message.data && message.data.tasks)
        {
          this.loadCardView(message.data.tasks);
        }
      }
    });
    this.settingService.getCachedSessionSettings().subscribe({
      next: (sessionSetting) =>
      {
        this.messagesEnabled = (sessionSetting.messagesRoles !== '') ?
          this.userService.isRoleInList(this.authenticationService.currentUser.role, sessionSetting.messagesRoles) : false;
      }
    });
  }

  @Input() public set data(tasks: any)
  {
    if (tasks === undefined)
    {
      return;
    }
    setTimeout(() =>
    {
      this.loadCardView(tasks);
    });
  }

  public ngOnInit(): void
  {
    this.roles = this.userService.roles;
    this.calendarTexts = this.taskUtility.calendarTexts;
  }

  public ngOnDestroy(): void
  {
    if (this.messagingSubscription !== undefined) { this.messagingSubscription.unsubscribe(); }
  }

  /**
   * Send a new message to the selected assignee.
   *
   * @param assignee Selected assignee.
   */
  public sendMessageToAssignee(assignee: string): void
  {
    this.messengerService.sendMessage(new MessageSubject('openComposeMessageDialog', { sendTo: assignee }));
  }

  /**
   * Add a new task to the selected assignee.
   *
   * @param assignee Selected assignee.
   */
  public addTaskForAssignee(assignee: string): void
  {
    const user = this.users.find(u => u.username === assignee);
    const assigneeId = user ? user.id : null;
    const messageData: any = {};
    messageData.assigneeId = assigneeId;
    this.messengerService.sendMessage(new MessageSubject('openAddTaskDialog', messageData));
  }

  /**
   * Open Task details dialog.
   *
   * @param task Current selected task.
   */
  public openTask(task: any, isCompleteMode = false): void
  {
    // Send message to open the task details on the selected task
    const messageData: any = {};
    messageData.task = task;
    messageData.autoComplete = isCompleteMode;
    this.messengerService.sendMessage(new MessageSubject('openTaskEditDialog', messageData));
  }

  /**
   * Provide the proper translation key to use for Assigned vs Unassigned tasks.
   *
   * @param data Task data.
   */
  public getAssignTranslationKey(data: any): string
  {
    if (data.assignedTasks === 1)
    {
      return data.isUnassigned ? 'TASK-CARD-VIEW-TASK-NOT-ASSIGNED' : 'TASK-CARD-VIEW-TASK-ASSIGNED';
    }
    else
    {
      return data.isUnassigned ? 'TASK-CARD-VIEW-TASKS-NOT-ASSIGNED' : 'TASK-CARD-VIEW-TASKS-ASSIGNED';
    }
  }

  /**
   * Set Card Data
   *
   * @param userTasks Tasks
   * @param cardData Card data to set
   */
  private setTaskCardData(userTasks: any, cardData: TaskCard)
  {
    let nextTaskDueDate = null;
    userTasks.forEach((task: any) =>
    {
      task.isLate = task.status === 'Open' && moment(task.dueDate).isBefore(moment(), 'day');
      switch (task.priority)
      {
        case 'Urgent':
          task.orderPriority = 1;
          break;
        case 'High':
          task.orderPriority = 2;
          break;
        case 'Medium':
          task.orderPriority = 3;
          break;
        case 'Low':
          task.orderPriority = 4;
          break;
      }
      cardData.lateTasks += task.isLate ? 1 : 0;
      cardData.assignedTasks += task.status === 'Open' ? 1 : 0;
      cardData.assigneeTeamRole = task.assigneeTeamRole;
      // Set the minimum task due date
      if (task.dueDate && task.status === 'Open' &&
        (!nextTaskDueDate || moment(task.dueDate).isBefore(moment(nextTaskDueDate), 'day')))
      {
        nextTaskDueDate = task.dueDate;
      }
    });

    // Show open first
    cardData.tasks = sortBy(userTasks, o => o.status === 'Open' ? '1' : '2' + `${o.dueDate}${o.orderPriority}`);
    cardData.nextTaskDueDate = nextTaskDueDate;
    const lang = this.languageService.currentLanguage;
    cardData.nextTaskDueDateText = moment(nextTaskDueDate).calendar(moment(), {
      sameDay: `[${this.calendarTexts[0].text}]`,
      nextDay: `[${this.calendarTexts[1].text}]`,
      nextWeek: 'dddd',
      lastDay: `[${this.calendarTexts[2].text}]`,
      lastWeek: lang === 'en' ? `[${this.calendarTexts[3].text}] dddd` : `dddd [${this.calendarTexts[3].text}]`,
      sameElse: 'M/D/YYYY'
    });
  }

  /**
   * Create a new TaskCard
   *
   * @param username Username (Assignee)
   * @param name User name
   * @param order Order
   */
  private createNewCard(name: string, username: string = null, orderUser: number = null, userRole: string = null): TaskCard
  {
    const cardData = new TaskCard();
    cardData.assignee = username;
    cardData.assigneeName = name;
    cardData.lateTasks = 0;
    cardData.assignedTasks = 0;
    cardData.orderUser = orderUser;
    cardData.isUnassigned = false;

    // Set the user role description
    if (userRole !== null)
    {
      const role = this.roles.find(r => r.value === userRole);
      cardData.userRoleName = role ? role.text : '';
    }

    return cardData;
  }

  /**
   * Load the card view from the loaded tasks.
   *
   * @param tasks Loaded tasks.
   */
  private loadCardView(tasks: any): void
  {
    this.translate.get('UNASSIGNED').subscribe({
      next: (res) =>
      {
        this.userService.getCacheUsersByChannelGroups(this.channelId).subscribe({
          next: (users) =>
          {
            this.users = users;
            const list = new TaskCardList();

            tasks.forEach(t =>
            {
              if (this.users.find(u => u.username === t.assignee) === undefined && t.assignee != null)
              {
                const user = new User();
                user.username = t.assignee;
                user.name = t.assigneeName;
                user.role = t.assigneeRole;
                this.users.push(user);
              }
            });

            let order = users.length;
            users.forEach(d =>
            {
              const cardData = this.createNewCard(d.name, d.username, order, d.role);
              order--;

              // Get the tasks associated with the user.
              const userTasks = tasks.filter(t => t.assignee === d.username);
              if (userTasks && userTasks.length > 0)
              {
                this.setTaskCardData(userTasks, cardData);
                list.data.push(cardData);
              }
            });

            // Apply sorting
            //  1. Late task first
            //  2. Highest number of tasks
            //  3. Assignee name using reverse order
            list.data = sortBy(list.data, o => (o.lateTasks * 10000) + (o.tasks.length * 1000) + o.orderUser).reverse();

            // If unassigned tasks, insert as first card
            const unassignedTasks = tasks.filter(t => t.assignee === null);
            if (unassignedTasks && unassignedTasks.length > 0)
            {
              const cardData = this.createNewCard(res);
              this.setTaskCardData(unassignedTasks, cardData);
              cardData.isUnassigned = true;
              list.data.splice(0, 0, cardData);
            }
            this.model = list;
          }
        });
      }
    });
  }
}
